import { cn, useBreakpoint } from "@tournated-com/ui";
import {
  ComponentProps,
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { useT } from "@/i18n";
import { renderInDashboardLayoutContent } from "@/layouts/DashboardLayout";
import { Button } from "@/ui";

type FormCardVariant = "default" | "nested";

/**
 * Layout variant shared by the FormCard primitives via context. "nested" is set
 * by {@link NestedFormPanel} for league-scoped child entities (tournament /
 * ranking categories) whose page renders ONE outer panel hosting the title +
 * tabs/steps: inside it each FormCard drops its own surface (no bg/border/
 * padding), its header stops sticking, and its title steps up from
 * `preset-label-xl` to `preset-title-lg`. Default keeps the standalone-card look.
 */
const FormCardVariantContext = createContext<FormCardVariant>("default");

const useFormCardVariant = () => useContext(FormCardVariantContext);

export type FormCardProps = ComponentProps<"section">;

export const FormCard = ({ children, className, ...props }: FormCardProps) => {
  const variant = useFormCardVariant();
  return (
    <section
      className={cn(
        "flex flex-col relative",
        variant === "default" && "bg-surface-1 border-surface-2 rounded-md p-4 sm:p-5 border",
        className
      )}
      {...props}
    >
      {children}
    </section>
  );
};

export type NestedFormPanelProps = ComponentProps<"section">;

/**
 * Page-level surface for a league-scoped child entity's tabbed / stepped form
 * (tournament / ranking categories). Hosts the page title + tab strip (or
 * stepper) + the active form card inside ONE panel, and provides the "nested"
 * FormCard variant to everything rendered within.
 */
export const NestedFormPanel = ({ children, className, ...props }: NestedFormPanelProps) => (
  <FormCardVariantContext.Provider value="nested">
    <section
      className={cn(
        "flex flex-col bg-surface-1 border-surface-2 rounded-md p-4 sm:p-5 border",
        className
      )}
      {...props}
    >
      {children}
    </section>
  </FormCardVariantContext.Provider>
);

export type NestedFormPanelHeaderProps = {
  /** The panel-level page title (an `h1`). */
  title: ReactNode;
  /**
   * Primary form actions (Save / Discard, or Back / Next / Create). In a nested
   * panel these live against the title, not inside each card — the per-card
   * header + floating actions are suppressed (see {@link FormCardActionsWrapper} /
   * {@link FormCardFloatingActions}). Usually a {@link FormCardActions} wired to
   * the active tab / step via `onSubmit`.
   */
  actions?: ReactNode;
  className?: string;
};

/**
 * Title row for a {@link NestedFormPanel}: the page `h1` on the left and the
 * primary form actions on the right, so a tabbed / stepped form's Save / Next
 * sits against the panel title instead of inside each card.
 */
export const NestedFormPanelHeader = ({
  title,
  actions,
  className,
}: NestedFormPanelHeaderProps) => (
  <div
    className={cn(
      "flex flex-col gap-4 mb-5 sm:mb-6 sm:flex-row sm:items-center sm:justify-between",
      className
    )}
  >
    <h1 className="preset-title-xl text-new-primary min-w-0">{title}</h1>
    {actions ? <div className="flex gap-2 sm:shrink-0">{actions}</div> : null}
  </div>
);

export type FormCardHeaderProps = ComponentProps<"div"> & {
  sticky?: boolean;
  stickyTop?: string;
};

export const FormCardHeader = ({
  children,
  className,
  sticky = true,
  stickyTop = "60px",
  ...props
}: FormCardHeaderProps) => {
  const variant = useFormCardVariant();
  // In a nested panel the header must not stick: it lives below the panel's own
  // title + tab strip, and its sticky negative margins assume the card owns the
  // surface padding (which it no longer does).
  const isSticky = sticky && variant !== "nested";
  const [isStuck, setIsStuck] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isSticky) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(([entry]) => {
      setIsStuck(!entry.isIntersecting);
    });

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [isSticky]);

  if (isSticky) {
    return (
      <>
        <div ref={sentinelRef} className="absolute -top-px h-px" />
        <div
          className={cn(
            "flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-5",
            "sticky z-10 -mx-4 -mt-4 px-4 pt-4 pb-5 sm:-mx-5 sm:-mt-5 sm:px-5 sm:pt-5",
            "border-b border-transparent bg-surface-1 rounded-t-md",
            isStuck && "border-surface-2",
            className
          )}
          style={{ top: stickyTop }}
          {...props}
        >
          {children}
        </div>
      </>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between",
        // Nested headers aren't sticky, so they need their own bottom spacing
        // (the sticky branch bakes `mb-5` into its padding).
        variant === "nested" && "mb-5",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

export type FormCardTitleWrapperProps = ComponentProps<"div">;

export const FormCardTitleWrapper = ({
  children,
  className,
  ...props
}: FormCardTitleWrapperProps) => {
  return (
    <div className={cn("flex flex-col gap-1 min-w-0", className)} {...props}>
      {children}
    </div>
  );
};

export type FormCardTitleProps = ComponentProps<"h2">;

export const FormCardTitle = ({ children, className, ...props }: FormCardTitleProps) => {
  const variant = useFormCardVariant();
  return (
    <h2
      className={cn(
        variant === "nested" ? "preset-title-lg" : "preset-label-xl",
        "text-new-primary",
        className
      )}
      {...props}
    >
      {children}
    </h2>
  );
};

export type FormCardDescriptionProps = ComponentProps<"p">;

export const FormCardDescription = ({
  children,
  className,
  ...props
}: FormCardDescriptionProps) => {
  return (
    <p className={cn("preset-body-lg text-secondary", className)} {...props}>
      {children}
    </p>
  );
};

export type FormCardActionsWrapperProps = ComponentProps<"div">;

export const FormCardActionsWrapper = ({
  children,
  className,
  ...props
}: FormCardActionsWrapperProps) => {
  const variant = useFormCardVariant();
  // In a nested panel the panel header owns the primary actions
  // (NestedFormPanelHeader), so the per-card header actions are suppressed.
  if (variant === "nested") return null;
  return (
    <div className={cn("hidden sm:flex sm:gap-2 sm:shrink-0", className)} {...props}>
      {children}
    </div>
  );
};
export type FormCardFloatingActions = {
  children: ReactNode;
  hideFloatingActions?: boolean;
};

export const FormCardFloatingActions = ({
  children,
  hideFloatingActions = false,
}: FormCardFloatingActions) => {
  const variant = useFormCardVariant();
  const isSmallScreen = useBreakpoint("sm", "down");
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // In a nested panel the panel header owns the primary actions
  // (NestedFormPanelHeader) on every breakpoint, so the mobile floating bar is
  // suppressed too.
  if (variant === "nested" || hideFloatingActions || !isSmallScreen || !isMounted) {
    return null;
  }

  const content = (
    <div className="bg-surface-1 flex items-center px-4 fixed bottom-0 left-0 py-5 w-full z-40 border-t border-solid border-surface-1">
      <div className="flex gap-2 w-full">{children}</div>
    </div>
  );

  return <>{renderInDashboardLayoutContent(content)}</>;
};

export type FormCardActionsMode = "create" | "update" | "next";

export type FormCardActions = {
  mode: FormCardActionsMode;
  /**
   * Form id the primary button submits via the `form` attribute — the in-card
   * usage. Optional because the panel-level usage (see {@link NestedFormPanelHeader})
   * lives OUTSIDE the form and drives it with `onSubmit` (`onPress`) instead.
   */
  formId?: string;
  /**
   * When set, the primary button submits via `onPress` instead of the `form`
   * attribute — used at panel level, where the actions sit outside the active
   * tab / step form. Pass the active control's `submit`.
   */
  onSubmit?: () => void;
  onDiscard?: () => void;
  onBack?: () => void;
  isLoading?: boolean;
  isDirty?: boolean;
};

export const FormCardActions = ({
  mode,
  formId,
  onSubmit,
  onDiscard,
  onBack,
  isLoading,
  isDirty = true,
}: FormCardActions) => {
  const t = useT();

  // Panel-level actions (with `onSubmit`) sit outside the form, so the primary
  // button presses instead of native-submitting; the in-card usage keeps the
  // `type="submit" form={id}` binding.
  const submitProps = onSubmit
    ? ({ onPress: onSubmit } as const)
    : ({ type: "submit", form: formId } as const);

  if (mode === "next") {
    return (
      <div className="flex flex-row gap-3 w-full sm:w-auto sm:gap-4">
        {onBack && (
          <Button
            variant="secondary"
            color="muted"
            size="lg"
            onPress={onBack}
            className="flex-1 sm:w-auto"
          >
            {t("general.actions.back")}
          </Button>
        )}
        <Button {...submitProps} variant="primary" size="lg" className="flex-1 sm:w-auto">
          {t("general.actions.next")}
        </Button>
      </div>
    );
  }

  if (mode === "create") {
    return (
      <Button
        {...submitProps}
        variant="primary"
        size="lg"
        className="w-full sm:w-auto"
        isLoading={isLoading}
        isDisabled={isLoading}
      >
        {t("general.actions.create")}
      </Button>
    );
  }

  if (mode === "update") {
    return (
      <>
        {isDirty && onDiscard && (
          <Button
            variant="secondary"
            color="muted"
            size="lg"
            onPress={onDiscard}
            className="flex-1 sm:flex-initial"
          >
            {t("general.actions.discardChanges")}
          </Button>
        )}
        <Button
          {...submitProps}
          variant="primary"
          color="brand"
          size="lg"
          isLoading={isLoading}
          isDisabled={!isDirty}
          className="flex-1 sm:flex-initial"
        >
          {t("general.actions.save")}
        </Button>
      </>
    );
  }

  return null;
};
